// The wording of the ProteoRisk results, in one place. It follows the
// ProteoRisk PDF report's English wording: results describe how closely a
// protein pattern resembles that of people with a condition, never that the
// reader has the condition.

export const STATUS_LABEL = Object.freeze({
  typical: 'Looks typical',
  moderate: 'Moderate pattern',
  high: 'Strong pattern',
});

export const TIER_LABEL = Object.freeze({
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
});

export const SECTION_COPY = Object.freeze({
  current: {
    title: 'Current findings',
    intro: 'How closely your protein pattern resembles that of people who had each condition when their blood was taken.',
  },
  ten_year: {
    title: '10-year risk',
    intro: 'How closely your protein pattern resembles that of people who developed each condition within ten years.',
  },
});

export const PROTEIN_STATE_LABEL = Object.freeze({
  above: 'Above typical range',
  below: 'Below typical range',
  typical: 'Typical',
});

export const NO_PANEL = 'None of the proteins that weigh most for this condition was outside its typical range.';

export const SCORE_FOOTNOTE = 'Scores describe how closely your protein pattern resembles that of people with each condition and are not a diagnosis.';

export const DOCTOR_NOTE = 'This report is not a diagnosis. We recommend sharing it with your doctor, who can look at your results together with your health history and other test results.';

export const DISCLAIMER = 'Protein levels alone are not enough to diagnose a specific disease. ProteoRisk compares your protein pattern with data from the general population and shows which conditions it resembles. It is not a diagnostic test, and resembling a pattern is not the same as having the condition.';

function plural(count, one, many) {
  return count === 1 ? one : many;
}

export function formatCount(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

export function formatPercent(fraction, digits = 1) {
  return new Intl.NumberFormat('en-US', {
    style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(fraction);
}

export function formatSigned(value, digits = 1) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits, maximumFractionDigits: digits, signDisplay: 'exceptZero',
  }).format(value);
}

export function headline(systems) {
  const total = systems.length;
  const flagged = systems.filter((system) => system.status !== 'typical').length;
  const typical = total - flagged;
  if (flagged === 0) return `Your blood proteins look typical in all ${total} body systems in this report.`;
  if (flagged === total) {
    return `Your blood proteins show patterns worth discussing with your doctor in all ${total} body systems in this report.`;
  }
  if (flagged === 1) {
    return `Your blood proteins look typical in ${typical} of ${total} body systems. One system shows a pattern worth discussing with your doctor.`;
  }
  return `Your blood proteins look typical in ${typical} of ${total} body systems. ${flagged} systems show patterns worth discussing with your doctor.`;
}

export function highlightSentence(finding) {
  return finding.section === 'current'
    ? `Your blood proteins look similar to those of people with ${finding.disease}.`
    : `Your blood proteins look similar to those of people who developed ${finding.disease} within ten years.`;
}

export function systemSentence(system) {
  if (!system.top) return 'Your proteins here look typical.';
  const parts = [system.top.section === 'current'
    ? `Your proteins look similar to those of people with ${system.top.disease}.`
    : `Your proteins look similar to those of people who developed ${system.top.disease} within ten years.`];
  if (system.others === 1) parts.push('One more condition here is worth discussing.');
  if (system.others > 1) parts.push(`${system.others} more conditions here are worth discussing.`);
  return parts.join(' ');
}

export function groupNote(group) {
  return `${group.notable.length} of ${group.total} moderate or high`;
}

export function lowTitle(count) {
  return `Low (${count})`;
}

export function resultsNote(counts) {
  const notable = counts.high + counts.moderate;
  const total = notable + counts.low;
  return `${notable} of ${total} ${plural(total, 'result', 'results')} moderate or high`;
}
